import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Table, Tag, Space, Typography, Button, message, Badge, Select, Tooltip, Empty } from 'antd';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  DesktopOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  WarningOutlined,
  ReloadOutlined,
  CloseCircleOutlined,
  BarChartOutlined,
  ThunderboltOutlined,
  FireOutlined,
  HistoryOutlined,
  DatabaseOutlined,
  ApiOutlined,
  SignalFilled,
  FileTextOutlined,
  DollarOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import websocketService from '../../services/websocket';
import { isRecent, getCurrentUTCTimestamp, safeGetRelativeTime, safeFormatTimestamp } from '../../utils/timezone';
import { handleApiError } from '../../utils/errorHandler';
import { useAuth } from '../../contexts/AuthContext';
import DeviceStatusChart from '../../components/Charts/DeviceStatusChart';
import UsageTrendChart from '../../components/Charts/UsageTrendChart';
import logger from '../../services/logger';
import dayjs from 'dayjs';
import { formatArea, formatGender, formatSkinType, formatHairType } from '../../utils/sessionEnums';

const { Title, Text } = Typography;

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isCustomer = user?.role?.name === 'customer';
  const [stats, setStats] = useState({ total_devices: 0, online: 0, offline: 0, error: 0 });
  const [sessionStats, setSessionStats] = useState({ total_sessions: 0, total_shots: 0, total_energy_consumed: 0, average_duration: 0 });
  const [sessionPeriod, setSessionPeriod] = useState('today');
  const [usageTrendData, setUsageTrendData] = useState([]);
  const [recentSessions, setRecentSessions] = useState([]);
  const [devices, setDevices] = useState([]);
  const [activeRentals, setActiveRentals] = useState([]);
  const [invoiceSummary, setInvoiceSummary] = useState({ pending_count: 0, pending_amount: 0, overdue_count: 0 });
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [systemHealth, setSystemHealth] = useState({ backend: 'healthy', database: 'unknown', mqtt: 'unknown', timescaledb: 'unknown' });

  const getSessionDateRange = (period) => {
    const now = dayjs();
    switch (period) {
      case 'today': return { start: now.startOf('day').toISOString(), end: now.endOf('day').toISOString() };
      case 'week': return { start: now.subtract(7, 'days').startOf('day').toISOString(), end: now.endOf('day').toISOString() };
      case 'month': return { start: now.subtract(30, 'days').startOf('day').toISOString(), end: now.endOf('day').toISOString() };
      default: return {};
    }
  };

  const getPeriodDays = (period) => {
    switch (period) {
      case 'today': return 1;
      case 'week': return 7;
      case 'month': return 30;
      default: return 30;
    }
  };

  useEffect(() => {
    fetchDashboardData();

    const token = localStorage.getItem('token');
    if (token && websocketService) {
      websocketService.connect(token);

      const unsubTelemetry = websocketService.subscribe('telemetry', (data) => {
        if (data.type === 'telemetry') {
          setDevices(prev => prev.map(d => d.id === data.device_id ? { ...d, last_seen: data.last_seen || getCurrentUTCTimestamp(), status: data.device_status || d.status } : d));
        }
      });

      const unsubStatus = websocketService.subscribe('device_status', (data) => {
        if (data.type === 'device_status') {
          setDevices(prev => {
            const updated = prev.map(d => d.id === data.device_id ? { ...d, status: data.status, last_seen: data.last_seen || getCurrentUTCTimestamp() } : d);
            setStats({
              total_devices: updated.length,
              online: updated.filter(d => d.status === 'online').length,
              offline: updated.filter(d => d.status === 'offline').length,
              error: updated.filter(d => d.status === 'error').length,
            });
            return updated;
          });
        }
      });

      return () => { unsubTelemetry(); unsubStatus(); };
    }
  }, []);

  // Session period değiştiğinde yeniden çek (stats + trend + recent sessions)
  useEffect(() => {
    fetchSessionStats();
    fetchTrend();
    fetchRecentSessions();
  }, [sessionPeriod]);

  // 30s auto-refresh: sessionPeriod değişince interval yeniden kurulur,
  // stale closure'ı önler ve filtre seçimini korur.
  useEffect(() => {
    const interval = setInterval(fetchDashboardData, 30000);
    return () => clearInterval(interval);
  }, [sessionPeriod]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      const devicesResponse = await axios.get('/api/v1/devices');
      const deviceList = Array.isArray(devicesResponse.data) ? devicesResponse.data : [];
      setDevices(deviceList);

      setStats({
        total_devices: deviceList.length,
        online: deviceList.filter(d => d.status === 'online').length,
        offline: deviceList.filter(d => d.status === 'offline').length,
        error: deviceList.filter(d => d.status === 'error').length
      });

      // Session stats + trend + recent sessions
      await Promise.all([fetchSessionStats(), fetchTrend(), fetchRecentSessions()]);

      if (isCustomer) {
        // Müşteri: aktif kiralamalar + fatura özeti
        try {
          const rentalsResponse = await axios.get('/api/v1/rentals', { params: { active_only: true } });
          setActiveRentals(Array.isArray(rentalsResponse.data) ? rentalsResponse.data : []);
        } catch { setActiveRentals([]); }

        try {
          const invoicesResponse = await axios.get('/api/v1/billing/invoices', { params: { limit: 200 } });
          const invoices = Array.isArray(invoicesResponse.data) ? invoicesResponse.data : (invoicesResponse.data?.invoices || []);
          const pending = invoices.filter(i => i.status === 'pending');
          const overdue = invoices.filter(i => i.status === 'overdue');
          const pendingAmount = [...pending, ...overdue].reduce((sum, i) => sum + parseFloat(i.total_amount || 0), 0);
          setInvoiceSummary({
            pending_count: pending.length,
            pending_amount: pendingAmount,
            overdue_count: overdue.length
          });
        } catch { setInvoiceSummary({ pending_count: 0, pending_amount: 0, overdue_count: 0 }); }
      } else {
        // System health - sadece admin roller
        try {
          const healthResponse = await axios.get('/api/v1/health/detailed');
          const h = healthResponse.data;
          setSystemHealth({
            backend: h.status === 'healthy' ? 'healthy' : 'unhealthy',
            database: h.database?.connected !== false ? 'healthy' : 'unhealthy',
            mqtt: h.mqtt?.connected !== false ? 'healthy' : 'unhealthy',
            timescaledb: h.timescaledb?.enabled !== false ? 'healthy' : 'unhealthy'
          });
        } catch {
          setSystemHealth({ backend: 'healthy', database: 'healthy', mqtt: 'healthy', timescaledb: 'healthy' });
        }
      }

      setLastUpdate(new Date());
    } catch (error) {
      logger.error('Dashboard fetch error:', error);
      handleApiError(error, 'Dashboard verileri yüklenirken hata', message.error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTrend = async () => {
    try {
      const days = getPeriodDays(sessionPeriod);
      const response = await axios.get('/api/v1/logs/device-sessions/daily-trend', { params: { days } });
      setUsageTrendData(Array.isArray(response.data) ? response.data : []);
    } catch { setUsageTrendData([]); }
  };

  const fetchRecentSessions = async () => {
    try {
      const range = getSessionDateRange(sessionPeriod);
      const params = { log_type: 'session', limit: 20 };
      if (range.start) params.start_time = range.start;
      if (range.end) params.end_time = range.end;
      const response = await axios.get('/api/v1/logs/device', { params });
      setRecentSessions(response.data?.logs || []);
    } catch { setRecentSessions([]); }
  };

  const fetchSessionStats = async () => {
    try {
      const range = getSessionDateRange(sessionPeriod);
      const params = {};
      if (range.start) params.start_time = range.start;
      if (range.end) params.end_time = range.end;
      const response = await axios.get('/api/v1/logs/device-sessions/stats', { params });
      setSessionStats(response.data);
    } catch {
      setSessionStats({ total_sessions: 0, total_shots: 0, total_energy_consumed: 0, average_duration: 0 });
    }
  };

  const getStatusTag = (status) => {
    const cfg = {
      online: { color: 'green', icon: <CheckCircleOutlined />, text: 'Çevrimiçi' },
      offline: { color: 'orange', icon: <ClockCircleOutlined />, text: 'Çevrimdışı' },
      error: { color: 'red', icon: <ExclamationCircleOutlined />, text: 'Hata' },
      maintenance: { color: 'blue', icon: <WarningOutlined />, text: 'Bakım' },
    };
    const c = cfg[status] || { color: 'default', text: status };
    return <Tag color={c.color} icon={c.icon}>{c.text}</Tag>;
  };

  const getHealthTag = (status, label) => {
    const color = status === 'healthy' ? 'green' : status === 'unhealthy' ? 'red' : 'orange';
    const icon = status === 'healthy' ? <CheckCircleOutlined /> : status === 'unhealthy' ? <CloseCircleOutlined /> : <ExclamationCircleOutlined />;
    const text = status === 'healthy' ? 'OK' : status === 'unhealthy' ? 'Hata' : '?';
    return (
      <span>
        {icon} <Text style={{ marginRight: 2, marginLeft: 4 }}>{label}</Text>
        <Tag color={color} style={{ margin: 0 }}>{text}</Tag>
      </span>
    );
  };

  const onlinePercent = stats.total_devices > 0 ? Math.round((stats.online / stats.total_devices) * 100) : 0;

  const sessionColumns = [
    {
      title: 'Zaman', dataIndex: 'session_start_time', key: 'time', width: 150,
      render: (t) => t ? safeFormatTimestamp(t) : '-'
    },
    {
      title: 'Cihaz', dataIndex: 'device_id', key: 'device', width: 140,
      render: (id) => { const d = devices.find(x => x.id === id); return d ? <Text strong>{d.name}</Text> : `#${id}`; }
    },
    {
      title: 'Bölge', dataIndex: 'session_area', key: 'area', width: 110,
      render: formatArea
    },
    {
      title: 'Cinsiyet', dataIndex: 'session_gender', key: 'gender', width: 80,
      render: formatGender
    },
    {
      title: 'Cilt', dataIndex: 'session_skin_type', key: 'skin_type', width: 80,
      render: formatSkinType
    },
    {
      title: 'Kıl', dataIndex: 'session_hair_type', key: 'hair_type', width: 100,
      render: formatHairType
    },
    {
      title: 'Atış', dataIndex: 'session_shots', key: 'shots', width: 70,
      render: (v) => v ?? '-'
    },
    {
      title: 'Süre', dataIndex: 'session_duration', key: 'duration', width: 90,
      render: (v) => v ? `${Math.floor(v / 60)}dk ${v % 60}sn` : '-'
    },
    {
      title: 'Joule', dataIndex: 'session_joule', key: 'joule', width: 80,
      render: (v) => v ? `${v} J` : '-'
    },
    {
      title: 'Hz', dataIndex: 'session_hz', key: 'hz', width: 60,
      render: (v) => v ? `${v}` : '-'
    },
  ];

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={2} style={{ margin: 0 }}>Dashboard</Title>
        <Space>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {lastUpdate.toLocaleTimeString('tr-TR')}
          </Text>
          <Button icon={<ReloadOutlined />} onClick={fetchDashboardData} loading={loading} size="small">Yenile</Button>
        </Space>
      </div>

      {/* Device Stats - Compact Cards */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={6}>
          <Card size="small" style={{ textAlign: 'center' }}>
            <DesktopOutlined style={{ fontSize: 20, color: '#1890ff', marginBottom: 4 }} />
            <div style={{ fontSize: 24, fontWeight: 700 }}>{stats.total_devices}</div>
            <Text type="secondary" style={{ fontSize: 12 }}>Toplam Cihaz</Text>
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" style={{ textAlign: 'center' }}>
            <CheckCircleOutlined style={{ fontSize: 20, color: '#52c41a', marginBottom: 4 }} />
            <div style={{ fontSize: 24, fontWeight: 700, color: '#52c41a' }}>{stats.online}</div>
            <Text type="secondary" style={{ fontSize: 12 }}>Çevrimiçi</Text>
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" style={{ textAlign: 'center' }}>
            <ClockCircleOutlined style={{ fontSize: 20, color: '#fa8c16', marginBottom: 4 }} />
            <div style={{ fontSize: 24, fontWeight: 700, color: '#fa8c16' }}>{stats.offline}</div>
            <Text type="secondary" style={{ fontSize: 12 }}>Çevrimdışı</Text>
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" style={{ textAlign: 'center' }}>
            <SignalFilled style={{ fontSize: 20, color: '#52c41a', marginBottom: 4 }} />
            <div style={{ fontSize: 24, fontWeight: 700 }}>{onlinePercent}%</div>
            <Text type="secondary" style={{ fontSize: 12 }}>Çevrimiçi Oran</Text>
          </Card>
        </Col>
      </Row>

      {/* Usage Stats - With Period Selector (aşağıdaki trend + son seansları da filtreler) */}
      <Card
        size="small"
        style={{ marginBottom: 16 }}
        title={
          <Space>
            <BarChartOutlined />
            <span>Kullanım İstatistikleri</span>
            <Tooltip title="Seçilen dönem kullanım trendi ve son seansları da filtreler">
              <InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: 12 }} />
            </Tooltip>
          </Space>
        }
        extra={
          <Select value={sessionPeriod} onChange={setSessionPeriod} size="small" style={{ width: 120 }}>
            <Select.Option value="today">Bugün</Select.Option>
            <Select.Option value="week">Son 7 Gün</Select.Option>
            <Select.Option value="month">Son 30 Gün</Select.Option>
            <Select.Option value="all">Tümü</Select.Option>
          </Select>
        }
      >
        <Space size="large" wrap>
          <span>
            <HistoryOutlined style={{ color: '#1890ff', marginRight: 6 }} />
            <Text style={{ marginRight: 4 }}>Toplam Seans</Text>
            <Text strong style={{ fontSize: 18 }}>{sessionStats.total_sessions || 0}</Text>
          </span>
          <span>
            <ThunderboltOutlined style={{ color: '#fa8c16', marginRight: 6 }} />
            <Text style={{ marginRight: 4 }}>Toplam Atış</Text>
            <Text strong style={{ fontSize: 18 }}>{sessionStats.total_shots || 0}</Text>
          </span>
          <span>
            <ClockCircleOutlined style={{ color: '#52c41a', marginRight: 6 }} />
            <Text style={{ marginRight: 4 }}>Ort. Süre</Text>
            <Text strong style={{ fontSize: 18 }}>{sessionStats.average_duration || 0}</Text>
            <Text> dk</Text>
          </span>
          <span>
            <FireOutlined style={{ color: '#f5222d', marginRight: 6 }} />
            <Text style={{ marginRight: 4 }}>Enerji</Text>
            <Text strong style={{ fontSize: 18 }}>{sessionStats.total_energy_consumed || 0}</Text>
            <Text> kWh</Text>
          </span>
        </Space>
      </Card>

      {/* Charts: Müşteri için sadece kullanım trendi tam genişlik, admin için 2'li grid */}
      {isCustomer ? (
        <div style={{ marginBottom: 16 }}>
          <UsageTrendChart data={usageTrendData} />
        </div>
      ) : (
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col xs={24} md={12}>
            <DeviceStatusChart data={stats} />
          </Col>
          <Col xs={24} md={12}>
            <UsageTrendChart data={usageTrendData} />
          </Col>
        </Row>
      )}

      {/* Son Seanslar (dinamik, tarih filtresine bağlı) */}
      <Card
        size="small"
        title={<Space><HistoryOutlined /><span>Son Seanslar</span></Space>}
        extra={<Button type="link" href="/devices" size="small">Tüm Cihazlar</Button>}
        style={{ marginBottom: 16 }}
      >
        <Table
          columns={sessionColumns}
          dataSource={recentSessions}
          rowKey={(r) => `${r.device_id}-${r.timestamp}`}
          pagination={false}
          loading={loading}
          size="small"
          locale={{ emptyText: 'Seçili dönemde seans kaydı yok' }}
        />
      </Card>

      {/* Müşteri: Aktif Kiralamalar + Fatura Özeti (sayfanın en altında, eşit boyut) */}
      {isCustomer && (
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }} align="stretch">
          <Col xs={24} md={12}>
            <Card
              size="small"
              title={<Space><DesktopOutlined /><span>Aktif Kiralamalar</span></Space>}
              extra={<Button type="link" size="small" onClick={() => navigate('/devices')}>Tüm Cihazlar</Button>}
              style={{ height: '100%' }}
            >
              {activeRentals.length === 0 ? (
                <Empty description="Aktif kiralama yok" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              ) : (
                <Table
                  dataSource={activeRentals}
                  rowKey="id"
                  pagination={false}
                  size="small"
                  columns={[
                    {
                      title: 'Cihaz', dataIndex: 'device_name', key: 'device_name',
                      render: (t, r) => <a onClick={() => navigate(`/devices/${r.device_id}`)}>{t || `#${r.device_id}`}</a>
                    },
                    {
                      title: 'Tip', dataIndex: 'billing_type', key: 'billing_type', width: 90,
                      render: (t) => <Tag color={t === 'fixed_monthly' ? 'blue' : 'purple'}>{t === 'fixed_monthly' ? 'Sabit' : 'Atış'}</Tag>
                    },
                    {
                      title: 'Ücret', key: 'rate', width: 120, align: 'right',
                      render: (_, r) => r.billing_type === 'fixed_monthly'
                        ? `₺${parseFloat(r.monthly_rate || 0).toFixed(2)}/ay`
                        : `₺${parseFloat(r.unit_price || 0).toFixed(4)}/atış`
                    },
                  ]}
                />
              )}
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card
              size="small"
              title={<Space><DollarOutlined /><span>Faturalandırma Özeti</span></Space>}
              extra={<Button type="link" size="small" onClick={() => navigate('/billing')}>Tüm Faturalar</Button>}
              style={{ height: '100%' }}
            >
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Space>
                    <FileTextOutlined style={{ color: '#fa8c16' }} />
                    <Text>Bekleyen Fatura</Text>
                  </Space>
                  <Text strong style={{ fontSize: 18, color: '#fa8c16' }}>{invoiceSummary.pending_count}</Text>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Space>
                    <ExclamationCircleOutlined style={{ color: '#f5222d' }} />
                    <Text>Gecikmiş Fatura</Text>
                  </Space>
                  <Text strong style={{ fontSize: 18, color: '#f5222d' }}>{invoiceSummary.overdue_count}</Text>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
                  <Space>
                    <DollarOutlined style={{ color: '#1890ff' }} />
                    <Text>Bekleyen Tutar</Text>
                  </Space>
                  <Text strong style={{ fontSize: 18 }}>₺{invoiceSummary.pending_amount.toFixed(2)}</Text>
                </div>
              </Space>
            </Card>
          </Col>
        </Row>
      )}

      {/* System Health - sadece admin roller */}
      {!isCustomer && (
        <Card size="small" title={<Space><DatabaseOutlined /><span>Sistem Durumu</span></Space>}>
          <Space size="large" wrap>
            {getHealthTag(systemHealth.backend, 'Backend')}
            {getHealthTag(systemHealth.database, 'Database')}
            {getHealthTag(systemHealth.mqtt, 'MQTT')}
            {getHealthTag(systemHealth.timescaledb, 'TimescaleDB')}
          </Space>
        </Card>
      )}
    </div>
  );
};

export default Dashboard;
